/**
 * Worker management module
 * Handles worker status, controls, and the Workers tab
 */

import { API } from '../core/api.js';
import { AppState } from '../core/state.js';
import { startPoller } from '../core/poller.js';
import { showNotification } from '../components/notifications.js';

let workerPoller = null;

/**
 * Start worker status check interval
 */
export function startWorkerStatusCheck() {
    if (workerPoller) workerPoller.stop();
    workerPoller = startPoller('workers', checkWorkerStatus, { baseMs: 10000, maxMs: 60000 });
}

/**
 * Stop worker status check interval
 */
export function stopWorkerStatusCheck() {
    if (workerPoller) {
        workerPoller.stop();
        workerPoller = null;
    }
}

/**
 * Check worker status and update indicators
 */
export async function checkWorkerStatus() {
    try {
        const status = await API.get('/api/workers/status');

        // Update status indicators
        const downloadDot = document.getElementById('download-status');
        const processDot = document.getElementById('process-status');
        const diarizationDot = document.getElementById('diarization-status');
        const analyticsDot = document.getElementById('analytics-status');

        if (downloadDot) {
            downloadDot.className = `status-dot ${status.download_worker.status === 'running' ? 'running' : 'stopped'}`;
        }
        if (processDot) {
            processDot.className = `status-dot ${status.process_worker.status === 'running' ? 'running' : 'stopped'}`;
        }
        if (diarizationDot) {
            diarizationDot.className = `status-dot ${status.diarization_worker.status === 'running' ? 'running' : 'stopped'}`;
        }
        if (analyticsDot) {
            analyticsDot.className = `status-dot ${status.analytics_worker.status === 'running' ? 'running' : 'stopped'}`;
        }
        const transcriptionFetchDot = document.getElementById('transcription-fetch-status');
        if (transcriptionFetchDot) {
            transcriptionFetchDot.className = `status-dot ${status.transcription_fetch_worker?.status === 'running' ? 'running' : 'stopped'}`;
        }

        // Update worker buttons if on workers tab
        if (AppState.currentTab === 'workers') {
            const downloadBtn = document.getElementById('download-worker-btn');
            const processBtn = document.getElementById('process-worker-btn');
            const diarizationBtn = document.getElementById('diarization-worker-btn');
            const analyticsBtn = document.getElementById('analytics-worker-btn');

            if (downloadBtn) {
                downloadBtn.textContent = status.download_worker.status === 'running' ? 'Stop' : 'Start';
            }
            if (processBtn) {
                processBtn.textContent = status.process_worker.status === 'running' ? 'Stop' : 'Start';
            }
            if (diarizationBtn) {
                diarizationBtn.textContent = status.diarization_worker.status === 'running' ? 'Stop' : 'Start';
            }
            if (analyticsBtn) {
                analyticsBtn.textContent = status.analytics_worker.status === 'running' ? 'Stop' : 'Start';
            }
            const transcriptionFetchBtn = document.getElementById('transcription-fetch-worker-btn');
            if (transcriptionFetchBtn) {
                transcriptionFetchBtn.textContent = status.transcription_fetch_worker?.status === 'running' ? 'Stop' : 'Start';
            }
        }
    } catch (error) {
        // Poller handles logging with backoff; rethrow so it can count failures.
        throw error;
    }
}

/**
 * Render worker details HTML (internal helper)
 */
function renderWorkerDetailsHTML(worker, countLabel = 'Episodes Processed') {
    const statusColor = worker.status === 'running' ? 'var(--success)' : 'var(--error)';
    const count = worker.episodes_downloaded ?? worker.episodes_processed ?? 0;
    let html = `<div style="color: var(--text-secondary);">`;
    html += `<p>Status: <span style="color: ${statusColor}">${worker.status}</span></p>`;
    html += `<p>${countLabel}: ${count}</p>`;
    html += `<p>Episodes Failed: ${worker.episodes_failed}</p>`;
    if (worker.last_run) {
        html += `<p>Last Run: ${new Date(worker.last_run).toLocaleString()}</p>`;
    }
    if (worker.current_task) {
        html += `<p>Current Task: ${worker.current_task}</p>`;
    }
    html += `</div>`;
    return html;
}

/**
 * Load worker details for the Workers tab
 */
export async function loadWorkerDetails() {
    try {
        const status = await API.get('/api/workers/status');

        // Render all worker details using helper
        const workers = [
            { id: 'download-worker-details', data: status.download_worker, label: 'Episodes Downloaded' },
            { id: 'process-worker-details', data: status.process_worker, label: 'Episodes Processed' },
            { id: 'diarization-worker-details', data: status.diarization_worker, label: 'Episodes Processed' },
            { id: 'analytics-worker-details', data: status.analytics_worker, label: 'Episodes Processed' },
            { id: 'transcription-fetch-worker-details', data: status.transcription_fetch_worker, label: 'Transcriptions Fetched' }
        ];

        workers.forEach(({ id, data, label }) => {
            const el = document.getElementById(id);
            if (el) {
                el.textContent = '';  // Clear safely
                el.insertAdjacentHTML('beforeend', renderWorkerDetailsHTML(data, label));
            }
        });

        // Update all buttons
        const buttons = [
            { id: 'download-worker-btn', worker: status.download_worker },
            { id: 'process-worker-btn', worker: status.process_worker },
            { id: 'diarization-worker-btn', worker: status.diarization_worker },
            { id: 'analytics-worker-btn', worker: status.analytics_worker },
            { id: 'transcription-fetch-worker-btn', worker: status.transcription_fetch_worker }
        ];

        buttons.forEach(({ id, worker }) => {
            const btn = document.getElementById(id);
            if (btn) {
                btn.textContent = worker.status === 'running' ? 'Stop' : 'Start';
            }
        });
    } catch (error) {
        console.error('Error loading worker details:', error);
    }
}

/**
 * Toggle download worker on/off
 */
export async function toggleDownloadWorker() {
    const button = document.getElementById('download-worker-btn');
    if (!button) return;

    const isRunning = button.textContent === 'Stop';

    button.disabled = true;
    button.innerHTML = '<span class="loading"></span>';

    try {
        const endpoint = isRunning ? '/api/workers/download/stop' : '/api/workers/download/start';
        await fetch(endpoint, { method: 'POST' });

        setTimeout(() => {
            button.disabled = false;
            loadWorkerDetails();
        }, 1000);
    } catch (error) {
        console.error('Error toggling download worker:', error);
        button.disabled = false;
        button.textContent = isRunning ? 'Stop' : 'Start';
    }
}

/**
 * Toggle process worker on/off
 */
export async function toggleProcessWorker() {
    const button = document.getElementById('process-worker-btn');
    if (!button) return;

    const isRunning = button.textContent === 'Stop';

    button.disabled = true;
    button.innerHTML = '<span class="loading"></span>';

    try {
        const endpoint = isRunning ? '/api/workers/process/stop' : '/api/workers/process/start';
        await fetch(endpoint, { method: 'POST' });

        setTimeout(() => {
            button.disabled = false;
            loadWorkerDetails();
        }, 1000);
    } catch (error) {
        console.error('Error toggling process worker:', error);
        button.disabled = false;
        button.textContent = isRunning ? 'Stop' : 'Start';
    }
}

/**
 * Toggle diarization worker on/off
 */
export async function toggleDiarizationWorker() {
    const button = document.getElementById('diarization-worker-btn');
    if (!button) return;

    const isRunning = button.textContent === 'Stop';

    button.disabled = true;
    button.textContent = '...';

    try {
        const endpoint = isRunning ? '/api/workers/diarization/stop' : '/api/workers/diarization/start';
        await fetch(endpoint, { method: 'POST' });

        setTimeout(() => {
            button.disabled = false;
            loadWorkerDetails();
        }, 1000);
    } catch (error) {
        console.error('Error toggling diarization worker:', error);
        button.disabled = false;
        button.textContent = isRunning ? 'Stop' : 'Start';
    }
}

/**
 * Toggle analytics worker on/off
 */
export async function toggleAnalyticsWorker() {
    const button = document.getElementById('analytics-worker-btn');
    if (!button) return;

    const isRunning = button.textContent === 'Stop';

    button.disabled = true;
    button.textContent = '...';

    try {
        const endpoint = isRunning ? '/api/workers/analytics/stop' : '/api/workers/analytics/start';
        await fetch(endpoint, { method: 'POST' });

        setTimeout(() => {
            button.disabled = false;
            loadWorkerDetails();
        }, 1000);
    } catch (error) {
        console.error('Error toggling analytics worker:', error);
        button.disabled = false;
        button.textContent = isRunning ? 'Stop' : 'Start';
    }
}

/**
 * Toggle transcription fetch worker on/off
 */
export async function toggleTranscriptionFetchWorker() {
    const button = document.getElementById('transcription-fetch-worker-btn');
    if (!button) return;

    const isRunning = button.textContent === 'Stop';

    button.disabled = true;
    button.textContent = '...';

    try {
        const endpoint = isRunning ? '/api/workers/transcription-fetch/stop' : '/api/workers/transcription-fetch/start';
        await fetch(endpoint, { method: 'POST' });

        setTimeout(() => {
            button.disabled = false;
            loadWorkerDetails();
        }, 1000);
    } catch (error) {
        console.error('Error toggling transcription fetch worker:', error);
        button.disabled = false;
        button.textContent = isRunning ? 'Stop' : 'Start';
    }
}

/**
 * Reset stuck transcription episodes
 */
export async function resetStuckEpisodes(buttonElement) {
    const button = buttonElement || event?.target;
    if (!button) return;

    button.disabled = true;
    button.innerHTML = '<span class="loading"></span> Resetting...';

    try {
        const response = await fetch('/api/transcriptions/reset-stuck', { method: 'POST' });
        const data = await response.json();

        if (response.ok) {
            showNotification(data.message, 'success');
            // Refresh stats to show updated counts
            if (window.loadStats) {
                window.loadStats();
            }
        } else {
            showNotification(data.detail || 'Failed to reset episodes', 'error');
        }
    } catch (error) {
        console.error('Error resetting stuck episodes:', error);
        showNotification('Error resetting stuck episodes', 'error');
    } finally {
        button.disabled = false;
        button.textContent = 'Reset';
    }
}

/**
 * Reset stuck LLM analytics episodes
 */
export async function resetStuckLLM(buttonElement) {
    const button = buttonElement || event?.target;
    if (!button) return;

    button.disabled = true;
    button.innerHTML = '<span class="loading"></span> Resetting...';

    try {
        const response = await fetch('/api/llm/reset-stuck', { method: 'POST' });
        const data = await response.json();

        if (response.ok) {
            showNotification(data.message, 'success');
            // Refresh stats to show updated counts
            if (window.loadStats) {
                window.loadStats();
            }
        } else {
            showNotification(data.detail || 'Failed to reset LLM episodes', 'error');
        }
    } catch (error) {
        console.error('Error resetting stuck LLM episodes:', error);
        showNotification('Error resetting stuck LLM episodes', 'error');
    } finally {
        button.disabled = false;
        button.textContent = 'Reset';
    }
}

// Track current issues filter
let currentIssuesFilter = 'failed';
let currentTranscriptionIssuesFilter = 'failed';

/**
 * Load LLM issues (failed or processing episodes)
 */
export async function loadLLMIssues(status = 'failed') {
    currentIssuesFilter = status;
    const container = document.getElementById('llm-issues-list');
    if (!container) return;

    // Update tab button styles
    const failedBtn = document.getElementById('issues-failed-btn');
    const processingBtn = document.getElementById('issues-processing-btn');
    const retryAllBtn = document.getElementById('retry-all-btn');

    if (failedBtn && processingBtn) {
        if (status === 'failed') {
            failedBtn.style.opacity = '1';
            failedBtn.classList.remove('btn-secondary');
            processingBtn.style.opacity = '0.7';
            processingBtn.classList.add('btn-secondary');
        } else {
            failedBtn.style.opacity = '0.7';
            failedBtn.classList.add('btn-secondary');
            processingBtn.style.opacity = '1';
            processingBtn.classList.remove('btn-secondary');
        }
    }

    // Show/hide retry all button
    if (retryAllBtn) {
        retryAllBtn.style.display = status === 'failed' ? 'block' : 'none';
    }

    container.innerHTML = '<div style="color: var(--text-secondary); padding: 1rem; text-align: center;">Loading...</div>';

    try {
        const response = await fetch(`/api/llm/issues?status=${status}&limit=50`);
        const data = await response.json();

        if (!data.issues || data.issues.length === 0) {
            container.innerHTML = `<div style="color: var(--text-secondary); padding: 1rem; text-align: center;">No ${status} episodes found</div>`;
            return;
        }

        container.innerHTML = '';
        data.issues.forEach(issue => {
            const item = document.createElement('div');
            item.style.cssText = 'padding: 0.75rem; background: var(--bg-secondary); border-radius: 8px; margin-bottom: 0.5rem;';

            const errorDisplay = issue.error || 'Unknown error';
            const errorTypeDisplay = issue.error_type ? `${issue.error_type}: ` : '';
            const modelDisplay = issue.model ? `Model: ${issue.model}` : '';
            const timeDisplay = issue.error_at ? formatTimeAgo(issue.error_at) : '';

            item.innerHTML = `
                <div style="display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem;">
                    <div style="flex: 1; min-width: 0;">
                        <div style="font-weight: 500; margin-bottom: 0.25rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                            ${escapeHtml(issue.title)}
                        </div>
                        <div style="color: var(--text-secondary); font-size: 0.8rem; margin-bottom: 0.25rem;">
                            ${escapeHtml(issue.podcast_title || '')}
                        </div>
                        <div style="color: var(--error); font-size: 0.85rem; word-break: break-word;">
                            ${escapeHtml(errorTypeDisplay)}${escapeHtml(errorDisplay)}
                        </div>
                        <div style="color: var(--text-secondary); font-size: 0.75rem; margin-top: 0.25rem;">
                            ${escapeHtml(modelDisplay)}${modelDisplay && timeDisplay ? ' · ' : ''}${timeDisplay}
                        </div>
                    </div>
                    <button class="btn btn-small" onclick="retryEpisodeLLM(${issue.episode_id})" style="flex-shrink: 0;">
                        Retry
                    </button>
                </div>
            `;
            container.appendChild(item);
        });
    } catch (error) {
        console.error('Error loading LLM issues:', error);
        container.innerHTML = '<div style="color: var(--error); padding: 1rem; text-align: center;">Error loading issues</div>';
    }
}

/**
 * Retry a single episode LLM processing
 */
export async function retryEpisodeLLM(episodeId) {
    try {
        const response = await fetch(`/api/episodes/${episodeId}/retry-llm`, { method: 'POST' });
        const data = await response.json();

        if (response.ok) {
            showNotification('Episode queued for retry', 'success');
            loadLLMIssues(currentIssuesFilter);
            if (window.loadStats) {
                window.loadStats();
            }
        } else {
            showNotification(data.detail || 'Failed to retry episode', 'error');
        }
    } catch (error) {
        console.error('Error retrying episode:', error);
        showNotification('Error retrying episode', 'error');
    }
}

/**
 * Retry all failed LLM episodes
 */
export async function retryAllFailedLLM() {
    const button = document.getElementById('retry-all-btn');
    if (button) {
        button.disabled = true;
        button.textContent = 'Retrying...';
    }

    try {
        const response = await fetch('/api/llm/retry-failed', { method: 'POST' });
        const data = await response.json();

        if (response.ok) {
            showNotification(data.message, 'success');
            loadLLMIssues('failed');
            if (window.loadStats) {
                window.loadStats();
            }
        } else {
            showNotification(data.detail || 'Failed to retry episodes', 'error');
        }
    } catch (error) {
        console.error('Error retrying all episodes:', error);
        showNotification('Error retrying episodes', 'error');
    } finally {
        if (button) {
            button.disabled = false;
            button.textContent = 'Retry All Failed';
        }
    }
}

/**
 * Format a timestamp as relative time
 */
function formatTimeAgo(isoString) {
    if (!isoString) return '';
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
}

/**
 * Escape HTML to prevent XSS
 */
function escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

/**
 * Load transcription issues (failed or processing episodes)
 */
export async function loadTranscriptionIssues(status = 'failed') {
    currentTranscriptionIssuesFilter = status;
    const container = document.getElementById('transcription-issues-list');
    if (!container) return;

    // Update tab button styles
    const failedBtn = document.getElementById('transcription-issues-failed-btn');
    const processingBtn = document.getElementById('transcription-issues-processing-btn');
    const retryAllBtn = document.getElementById('transcription-retry-all-btn');

    if (failedBtn && processingBtn) {
        if (status === 'failed') {
            failedBtn.style.opacity = '1';
            failedBtn.classList.remove('btn-secondary');
            processingBtn.style.opacity = '0.7';
            processingBtn.classList.add('btn-secondary');
        } else {
            failedBtn.style.opacity = '0.7';
            failedBtn.classList.add('btn-secondary');
            processingBtn.style.opacity = '1';
            processingBtn.classList.remove('btn-secondary');
        }
    }

    // Show/hide retry all button
    if (retryAllBtn) {
        retryAllBtn.style.display = status === 'failed' ? 'block' : 'none';
    }

    container.innerHTML = '<div style="color: var(--text-secondary); padding: 1rem; text-align: center;">Loading...</div>';

    try {
        const response = await fetch(`/api/transcriptions/issues?status=${status}&limit=50`);
        const data = await response.json();

        if (!data.issues || data.issues.length === 0) {
            container.innerHTML = `<div style="color: var(--text-secondary); padding: 1rem; text-align: center;">No ${status} episodes found</div>`;
            return;
        }

        container.innerHTML = '';
        data.issues.forEach(issue => {
            const item = document.createElement('div');
            item.style.cssText = 'padding: 0.75rem; background: var(--bg-secondary); border-radius: 8px; margin-bottom: 0.5rem;';

            const errorDisplay = issue.error || 'Unknown error';
            const errorTypeDisplay = issue.error_type ? `${issue.error_type}: ` : '';
            const timeDisplay = issue.error_at ? `Failed ${formatTimeAgo(issue.error_at)}` : '';

            item.innerHTML = `
                <div style="display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem;">
                    <div style="flex: 1; min-width: 0;">
                        <div style="font-weight: 500; margin-bottom: 0.25rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">
                            ${escapeHtml(issue.title)}
                        </div>
                        <div style="color: var(--text-secondary); font-size: 0.8rem; margin-bottom: 0.25rem;">
                            ${escapeHtml(issue.podcast_title || '')}
                        </div>
                        <div style="color: var(--error); font-size: 0.85rem; word-break: break-word;">
                            ${escapeHtml(errorTypeDisplay)}${escapeHtml(errorDisplay)}
                        </div>
                        ${timeDisplay ? `<div style="color: var(--text-secondary); font-size: 0.75rem; margin-top: 0.25rem;">${timeDisplay}</div>` : ''}
                    </div>
                    <button class="btn btn-small" onclick="retryEpisodeTranscription(${issue.episode_id})" style="flex-shrink: 0;">
                        Retry
                    </button>
                </div>
            `;
            container.appendChild(item);
        });
    } catch (error) {
        console.error('Error loading transcription issues:', error);
        container.innerHTML = '<div style="color: var(--error); padding: 1rem; text-align: center;">Error loading issues</div>';
    }
}

/**
 * Retry a single episode transcription
 */
export async function retryEpisodeTranscription(episodeId) {
    try {
        const response = await fetch(`/api/episodes/${episodeId}/retry-transcription`, { method: 'POST' });
        const data = await response.json();

        if (response.ok) {
            showNotification('Episode queued for transcription retry', 'success');
            loadTranscriptionIssues(currentTranscriptionIssuesFilter);
            if (window.loadStats) {
                window.loadStats();
            }
        } else {
            showNotification(data.detail || 'Failed to retry episode', 'error');
        }
    } catch (error) {
        console.error('Error retrying episode transcription:', error);
        showNotification('Error retrying episode', 'error');
    }
}

/**
 * Retry all failed transcription episodes
 */
export async function retryAllFailedTranscriptions() {
    const button = document.getElementById('transcription-retry-all-btn');
    if (button) {
        button.disabled = true;
        button.textContent = 'Retrying...';
    }

    try {
        const response = await fetch('/api/transcriptions/retry-failed', { method: 'POST' });
        const data = await response.json();

        if (response.ok) {
            showNotification(data.message, 'success');
            loadTranscriptionIssues('failed');
            if (window.loadStats) {
                window.loadStats();
            }
        } else {
            showNotification(data.detail || 'Failed to retry episodes', 'error');
        }
    } catch (error) {
        console.error('Error retrying all transcriptions:', error);
        showNotification('Error retrying transcriptions', 'error');
    } finally {
        if (button) {
            button.disabled = false;
            button.textContent = 'Retry All Failed';
        }
    }
}
